import React, { useState, useEffect, useContext } from 'react';
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";
import {
    GitBranch,
    AlertCircle,
    CheckCircle,
    X,
    Users,
    Shield,
    ArrowRight,
    Filter,
    Trash2,
    Edit,
    Eye
} from 'lucide-react';



// Import theme context for dark/light mode
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";
import AlertModal from "../components/AlertModal";
import ReusableDropdown from "../components/ReusableDropdown";

// Responsive helper functions
const getScreenSize = () => {
    const width = window.innerWidth;
    if (width <= 480) return "xs";
    if (width <= 768) return "sm";
    if (width <= 1024) return "md";
    if (width <= 1366) return "lg";
    if (width <= 1920) return "xl";
    return "xxl";
};

const getResponsiveSpacing = (screenSize) => {
    const spacingMap = {
        xs: { padding: "0.5rem", margin: "0.25rem", gap: "0.5rem" },
        sm: { padding: "0.75rem", margin: "0.5rem", gap: "0.75rem" },
        md: { padding: "1rem", margin: "0.75rem", gap: "1rem" },
        lg: { padding: "1.25rem", margin: "1rem", gap: "1.25rem" },
        xl: { padding: "1.5rem", margin: "1.25rem", gap: "1.5rem" },
        xxl: { padding: "2rem", margin: "1.5rem", gap: "2rem" },
    };
    return spacingMap[screenSize] || spacingMap.lg;
};

const getResponsiveFontSizes = (screenSize) => {
    const fontMap = {
        xs: { title: "1.25rem", subtitle: "0.875rem", body: "0.75rem", small: "0.6875rem", tiny: "0.625rem" },
        sm: { title: "1.5rem", subtitle: "1rem", body: "0.875rem", small: "0.75rem", tiny: "0.6875rem" },
        md: { title: "1.75rem", subtitle: "1.125rem", body: "0.9375rem", small: "0.8125rem", tiny: "0.75rem" },
        lg: { title: "2rem", subtitle: "1.25rem", body: "1rem", small: "0.875rem", tiny: "0.8125rem" },
        xl: { title: "2.25rem", subtitle: "1.375rem", body: "1rem", small: "0.875rem", tiny: "0.8125rem" },
        xxl: { title: "2.5rem", subtitle: "1.5rem", body: "1.125rem", small: "1rem", tiny: "0.875rem" },
    };
    return fontMap[screenSize] || fontMap.lg;
};

function EscalationGroupMapping() {
    const apiBaseUrl = process.env.REACT_APP_BASE_URL;

    const { theme } = useContext(ThemeContext);
    const { language } = useContext(LanguageContext);

    const [screenSize, setScreenSize] = useState(getScreenSize());
    const [spacing, setSpacing] = useState(getResponsiveSpacing(screenSize));
    const [fonts, setFonts] = useState(getResponsiveFontSizes(screenSize));

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const { profile, profileLoading } = useProfile();
    const userRole = profile?.role ?? "";

    // AlertModal state
    const [alertModal, setAlertModal] = useState({
        open: false,
        title: '',
        message: '',
        variant: 'info',
    });

    // Form state
    const [formData, setFormData] = useState({
        escalation_type: "",
        issuerGroupId: "",
        priority: "",
        assignedGroupId: ""
    });

    const [formErrors, setFormErrors] = useState({});

    const escalationTypes = [
        'ABUSIVE BEHAVIOUR',
        'EMPLOYEE COMPLAIN',
        'TICKET ISSUE',
        'POLICY VIOLATION',
        'TECHNICAL ISSUE',
        'OTHER'
    ];

    const priorities = ['low', 'medium', 'high'];

    const text = {
        en: {
            title: "Escalation Group Mapping",
            subtitle: "Configure escalation routing based on type and priority",
            escalationType: "Escalation Type",
            selectType: "Select escalation type",
            issuerGroup: "Issuer Group",
            selectIssuerGroup: "Select issuer group",
            priority: "Priority",
            selectPriority: "Select priority",
            assignedGroup: "Assigned Group",
            selectAssignedGroup: "Select assigned group",
            mapEscalation: "Map Escalation",
            cancel: "Cancel",
            loading: "Loading...",
            mappingSuccess: "Escalation mapping created successfully!",
            mappingFailed: "Failed to create escalation mapping",
            unauthorized: "You must be an admin to perform this action",
            typeRequired: "Escalation type is required",
            issuerGroupRequired: "Issuer group is required",
            priorityRequired: "Priority is required",
            assignedGroupRequired: "Assigned group is required",
            groupId: "Group ID",
            groupName: "Group Name",
            creator: "Creator",
            adminOnly: "Admin Only Feature",
            noGroups: "No groups available",
            loadGroups: "Please create groups first",
            mappingInfo: "Map escalation types to specific groups based on priority levels. This ensures escalations are automatically routed to the appropriate handling team.",
        },
        ar: {
            title: "ربط التصعيدات بالمجموعات",
            subtitle: "تكوين توجيه التصعيد بناءً على النوع والأولوية",
            escalationType: "نوع التصعيد",
            selectType: "اختر نوع التصعيد",
            issuerGroup: "مجموعة المُصدر",
            selectIssuerGroup: "اختر مجموعة المُصدر",
            priority: "الأولوية",
            selectPriority: "اختر الأولوية",
            assignedGroup: "المجموعة المعينة",
            selectAssignedGroup: "اختر المجموعة المعينة",
            mapEscalation: "ربط التصعيد",
            cancel: "إلغاء",
            loading: "جاري التحميل...",
            mappingSuccess: "تم إنشاء ربط التصعيد بنجاح!",
            mappingFailed: "فشل في إنشاء ربط التصعيد",
            unauthorized: "يجب أن تكون مسؤولاً لتنفيذ هذا الإجراء",
            typeRequired: "نوع التصعيد مطلوب",
            issuerGroupRequired: "مجموعة المُصدر مطلوبة",
            priorityRequired: "الأولوية مطلوبة",
            assignedGroupRequired: "المجموعة المعينة مطلوبة",
            groupId: "معرف المجموعة",
            groupName: "اسم المجموعة",
            creator: "المنشئ",
            adminOnly: "ميزة خاصة بالمسؤولين",
            noGroups: "لا توجد مجموعات متاحة",
            loadGroups: "يرجى إنشاء المجموعات أولاً",
            mappingInfo: "ربط أنواع التصعيد بمجموعات محددة بناءً على مستويات الأولوية. يضمن ذلك توجيه التصعيدات تلقائيًا إلى الفريق المناسب.",
        }
    };

    useEffect(() => {
        const handleResize = () => {
            const newScreenSize = getScreenSize();
            setScreenSize(newScreenSize);
            setSpacing(getResponsiveSpacing(newScreenSize));
            setFonts(getResponsiveFontSizes(newScreenSize));
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (userRole === "admin" || userRole === "superadmin") {
            fetchGroups();
        }
    }, [userRole]);

    const fetchGroups = async () => {
        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/groups/`, { method: "GET" });
            const data = await res.json();
            setGroups(data || []);
        } catch (err) {
            console.error('Error fetching groups:', err);
            setAlertModal({
                open: true,
                title: text[language].mappingFailed,
                message: "Failed to load groups",
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.escalation_type) {
            errors.escalation_type = text[language].typeRequired;
        }
        if (!formData.issuerGroupId) {
            errors.issuerGroupId = text[language].issuerGroupRequired;
        }
        if (!formData.priority) {
            errors.priority = text[language].priorityRequired;
        }
        if (!formData.assignedGroupId) {
            errors.assignedGroupId = text[language].assignedGroupRequired;
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (userRole !== "admin" && userRole !== "superadmin") {
            setAlertModal({
                open: true,
                title: text[language].adminOnly,
                message: text[language].unauthorized,
                variant: 'error',
            });
            return;
        }

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/escalations/map-escalation`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData)
            });

            if (!res.ok) throw new Error("Failed to create mapping");

            setAlertModal({
                open: true,
                title: text[language].mappingSuccess,
                message: text[language].mappingSuccess,
                variant: 'success',
            });

            // Reset form
            setFormData({
                escalation_type: "",
                issuerGroupId: "",
                priority: "",
                assignedGroupId: ""
            });
            setFormErrors({});
        } catch (err) {
            console.error('Mapping error:', err);
            setAlertModal({
                open: true,
                title: text[language].mappingFailed,
                message: text[language].mappingFailed,
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error for this field
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const getGroupById = (groupId) => {
        return groups.find(g => g.groupId === groupId);
    };

    const getPriorityBadge = (priority) => {
        const baseStyle = {
            padding: "0.25rem 0.625rem",
            borderRadius: "0.75rem",
            fontSize: fonts.small,
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.25rem",
        };

        const priorityConfig = {
            low: {
                bg: "#e0f2fe",
                color: "#0369a1",
                text: "Low",
            },
            medium: {
                bg: "#fef3c7",
                color: "#92400e",
                text: "Medium",
            },
            high: {
                bg: "#fee2e2",
                color: "#991b1b",
                text: "High",
            },
        };

        const config = priorityConfig[priority] || priorityConfig.medium;

        return (
            <span
                style={{
                    ...baseStyle,
                    backgroundColor: config.bg,
                    color: config.color,
                }}
            >
                {config.text}
            </span>
        );
    };

    const styles = {
        container: {
            direction: language === "ar" ? "rtl" : "ltr",
        },
        header: {
            background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
            border: theme === "dark"
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.05)",
            borderRadius: screenSize === "xs" ? "1rem" : "1.5rem",
            padding: spacing.padding,
            marginBottom: spacing.margin,
            boxShadow: theme === "dark"
                ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
                : "0 0.5rem 2rem rgba(0,0,0,0.1)",
        },
        title: {
            color: theme === "dark" ? "white" : "#1a1a1a",
            fontSize: "28px",
            fontWeight: "700",
            margin: 0,
            marginBottom: "0.5rem",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
        },
        subtitle: {
            color: theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(26, 26, 26, 0.7)",
            fontSize: fonts.subtitle,
            margin: 0,
        },
        infoBox: {
            background: theme === "dark"
                ? "rgba(59, 130, 246, 0.1)"
                : "rgba(59, 130, 246, 0.05)",
            border: "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "0.75rem",
            padding: spacing.padding,
            marginBottom: spacing.margin,
            display: "flex",
            alignItems: "flex-start",
            gap: "0.75rem",
        },
        formCard: {
            background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
            border: theme === "dark"
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.05)",
            borderRadius: screenSize === "xs" ? "1rem" : "1.5rem",
            padding: screenSize === "xs" ? "1.5rem" : "2rem",
            boxShadow: theme === "dark"
                ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
                : "0 0.5rem 2rem rgba(0,0,0,0.1)",
        },
        formGrid: {
            display: "grid",
            gridTemplateColumns: screenSize === "xs"
                ? "1fr"
                : screenSize === "sm"
                    ? "1fr"
                    : "repeat(2, 1fr)",
            gap: spacing.gap,
            marginBottom: spacing.margin,
        },
        formGroup: {
            marginBottom: spacing.margin,
        },
        formLabel: {
            display: "block",
            color: theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(26, 26, 26, 0.7)",
            fontSize: fonts.body,
            fontWeight: "500",
            marginBottom: "0.5rem",
        },
        errorText: {
            color: "#ef4444",
            fontSize: fonts.small,
            marginTop: "0.25rem",
            display: "block",
        },
        groupOption: {
            padding: "0.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.25rem",
        },
        groupInfo: {
            fontSize: fonts.tiny,
            opacity: 0.7,
        },
        buttonGroup: {
            display: "grid",
            gridTemplateColumns: screenSize === "xs" ? "1fr" : "1fr 1fr",
            gap: spacing.gap,
            marginTop: spacing.margin,
        },
        submitBtn: {
            background: "linear-gradient(135deg, #2e7cba 0%, #1e5a8e 100%)",
            color: "white",
            border: "none",
            padding: screenSize === "xs" ? "0.75rem 1.5rem" : "0.875rem 2rem",
            borderRadius: "0.75rem",
            cursor: "pointer",
            fontSize: fonts.body,
            fontWeight: "600",
            transition: "all 0.3s ease",
            boxShadow: "0 0.25rem 0.75rem rgba(46, 124, 186, 0.3)",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
        },
        cancelBtn: {
            background: theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
            color: theme === "dark" ? "white" : "#1a1a1a",
            border: theme === "dark"
                ? "1px solid rgba(255, 255, 255, 0.2)"
                : "1px solid rgba(0, 0, 0, 0.1)",
            padding: screenSize === "xs" ? "0.75rem 1.5rem" : "0.875rem 2rem",
            borderRadius: "0.75rem",
            cursor: "pointer",
            fontSize: fonts.body,
            fontWeight: "600",
            transition: "all 0.3s ease",
            width: "100%",
        },
        emptyState: {
            textAlign: "center",
            padding: screenSize === "xs" ? "3rem 1rem" : "4rem 2rem",
            color: theme === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(26, 26, 26, 0.5)",
        },
        previewCard: {
            background: theme === "dark"
                ? "rgba(34, 197, 94, 0.1)"
                : "rgba(34, 197, 94, 0.05)",
            border: "1px solid rgba(34, 197, 94, 0.2)",
            borderRadius: "0.75rem",
            padding: spacing.padding,
            marginTop: spacing.margin,
        },
        mappingFlow: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: spacing.gap,
        },
        flowItem: {
            flex: 1,
            minWidth: screenSize === "xs" ? "100%" : "150px",
            background: theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.9)",
            padding: "1rem",
            borderRadius: "0.75rem",
            border: theme === "dark"
                ? "1px solid rgba(255,255,255,0.1)"
                : "1px solid rgba(0,0,0,0.1)",
        },
        flowArrow: {
            color: "#2e7cba",
            display: screenSize === "xs" ? "none" : "block",
        },
    };

    // Show spinner while profile is loading
    if (profileLoading) {
        return (
            <div style={{ ...styles.container, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "200px" }}>
                <div style={{ fontSize: fonts.body, opacity: 0.6 }}>{text[language].loading}</div>
            </div>
        );
    }

    // Check if user is admin
    if (userRole !== "admin" && userRole !== "superadmin") {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <h1 style={styles.title}>
                        <Shield size={28} />
                        {text[language].adminOnly}
                    </h1>
                </div>
                <div style={styles.emptyState}>
                    <Shield size={64} style={{ opacity: 0.3, marginBottom: "1rem" }} />
                    <div style={{ fontSize: fonts.subtitle, fontWeight: "600" }}>
                        {text[language].unauthorized}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* AlertModal for errors and success */}
            <AlertModal
                open={alertModal.open}
                title={alertModal.title}
                message={alertModal.message}
                variant={alertModal.variant}
                onConfirm={() => setAlertModal({ ...alertModal, open: false })}
                onClose={() => setAlertModal({ ...alertModal, open: false })}
                theme={theme}
                language={language}
            />

            {/* Header */}
            <div style={styles.header}>
                <h1 style={styles.title}>
                    <GitBranch size={28} />
                    {text[language].title}
                </h1>
                <p style={styles.subtitle}>{text[language].subtitle}</p>
            </div>

            {/* Info Box */}
            <div style={styles.infoBox}>
                <AlertCircle size={20} color="#3b82f6" style={{ flexShrink: 0, marginTop: "0.125rem" }} />
                <div style={{ fontSize: fonts.body, color: "#3b82f6" }}>
                    {text[language].mappingInfo}
                </div>
            </div>

            {/* Main Form */}
            <div style={styles.formCard}>
                {loading && groups.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={{ fontSize: fonts.subtitle }}>{text[language].loading}</div>
                    </div>
                ) : groups.length === 0 ? (
                    <div style={styles.emptyState}>
                        <Users size={64} style={{ opacity: 0.3, marginBottom: "1rem" }} />
                        <div style={{ fontSize: fonts.subtitle, fontWeight: "600" }}>
                            {text[language].noGroups}
                        </div>
                        <div style={{ fontSize: fonts.body, marginTop: "0.5rem" }}>
                            {text[language].loadGroups}
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div style={styles.formGrid}>
                            {/* Escalation Type */}
                            <div style={styles.formGroup}>
                                <label style={styles.formLabel}>
                                    {text[language].escalationType} *
                                </label>
                                <ReusableDropdown
                                    items={escalationTypes.map(type => ({ key: type, label: type }))}
                                    value={formData.escalation_type}
                                    onChange={(val) => handleInputChange('escalation_type', val)}
                                    placeholder={text[language].selectType}
                                    getItemKey={(item) => item.key}
                                    getItemLabel={(item) => item.label}
                                    theme={theme}
                                    language={language}
                                    showAllOnArrowClick={true}
                                />
                                {formErrors.escalation_type && (
                                    <span style={styles.errorText}>{formErrors.escalation_type}</span>
                                )}
                            </div>

                            {/* Priority */}
                            <div style={styles.formGroup}>
                                <label style={styles.formLabel}>
                                    {text[language].priority} *
                                </label>
                                <ReusableDropdown
                                    items={priorities.map(p => ({ key: p, label: p.charAt(0).toUpperCase() + p.slice(1) }))}
                                    value={formData.priority}
                                    onChange={(val) => handleInputChange('priority', val)}
                                    placeholder={text[language].selectPriority}
                                    getItemKey={(item) => item.key}
                                    getItemLabel={(item) => item.label}
                                    theme={theme}
                                    language={language}
                                    showAllOnArrowClick={true}
                                />
                                {formErrors.priority && (
                                    <span style={styles.errorText}>{formErrors.priority}</span>
                                )}
                            </div>
                        </div>

                        <div style={styles.formGrid}>
                            {/* Issuer Group */}
                            <div style={styles.formGroup}>
                                <label style={styles.formLabel}>
                                    {text[language].issuerGroup} *
                                </label>
                                <ReusableDropdown
                                    items={groups}
                                    value={formData.issuerGroupId}
                                    onChange={(val) => handleInputChange('issuerGroupId', val)}
                                    placeholder={text[language].selectIssuerGroup}
                                    getItemKey={(item) => item.groupId}
                                    getItemLabel={(item) => `${item.groupName} (ID: ${item.groupId.slice(0, 8)}...) - Creator: ${item.creatorSr}`}
                                    theme={theme}
                                    language={language}
                                    showAllOnArrowClick={true}
                                />
                                {formErrors.issuerGroupId && (
                                    <span style={styles.errorText}>{formErrors.issuerGroupId}</span>
                                )}
                            </div>

                            {/* Assigned Group */}
                            <div style={styles.formGroup}>
                                <label style={styles.formLabel}>
                                    {text[language].assignedGroup} *
                                </label>
                                <ReusableDropdown
                                    items={groups}
                                    value={formData.assignedGroupId}
                                    onChange={(val) => handleInputChange('assignedGroupId', val)}
                                    placeholder={text[language].selectAssignedGroup}
                                    getItemKey={(item) => item.groupId}
                                    getItemLabel={(item) => `${item.groupName} (ID: ${item.groupId.slice(0, 8)}...) - Creator: ${item.creatorSr}`}
                                    theme={theme}
                                    language={language}
                                    showAllOnArrowClick={true}
                                />
                                {formErrors.assignedGroupId && (
                                    <span style={styles.errorText}>{formErrors.assignedGroupId}</span>
                                )}
                            </div>
                        </div>

                        {/* Preview Mapping Flow */}
                        {formData.escalation_type && formData.issuerGroupId && formData.assignedGroupId && formData.priority && (
                            <div style={styles.previewCard}>
                                <h4 style={{
                                    margin: "0 0 1rem 0",
                                    color: "#22c55e",
                                    fontSize: fonts.subtitle,
                                    fontWeight: "600"
                                }}>
                                    Mapping Preview
                                </h4>
                                <div style={styles.mappingFlow}>
                                    <div style={styles.flowItem}>
                                        <div style={{ fontSize: fonts.tiny, opacity: 0.7, marginBottom: "0.25rem" }}>
                                            Type & Priority
                                        </div>
                                        <div style={{ fontWeight: "600", fontSize: fonts.body }}>
                                            {formData.escalation_type}
                                        </div>
                                        <div style={{ marginTop: "0.5rem" }}>
                                            {getPriorityBadge(formData.priority)}
                                        </div>
                                    </div>

                                    <ArrowRight size={24} style={styles.flowArrow} />

                                    <div style={styles.flowItem}>
                                        <div style={{ fontSize: fonts.tiny, opacity: 0.7, marginBottom: "0.25rem" }}>
                                            From (Issuer)
                                        </div>
                                        <div style={{ fontWeight: "600", fontSize: fonts.body }}>
                                            {getGroupById(formData.issuerGroupId)?.groupName}
                                        </div>
                                        <div style={{ fontSize: fonts.tiny, opacity: 0.7, marginTop: "0.25rem" }}>
                                            {getGroupById(formData.issuerGroupId)?.creatorSr}
                                        </div>
                                    </div>

                                    <ArrowRight size={24} style={styles.flowArrow} />

                                    <div style={styles.flowItem}>
                                        <div style={{ fontSize: fonts.tiny, opacity: 0.7, marginBottom: "0.25rem" }}>
                                            To (Assigned)
                                        </div>
                                        <div style={{ fontWeight: "600", fontSize: fonts.body }}>
                                            {getGroupById(formData.assignedGroupId)?.groupName}
                                        </div>
                                        <div style={{ fontSize: fonts.tiny, opacity: 0.7, marginTop: "0.25rem" }}>
                                            {getGroupById(formData.assignedGroupId)?.creatorSr}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Buttons */}
                        <div style={styles.buttonGroup}>
                            <button
                                type="button"
                                onClick={() => {
                                    setFormData({
                                        escalation_type: "",
                                        issuerGroupId: "",
                                        priority: "",
                                        assignedGroupId: ""
                                    });
                                    setFormErrors({});
                                }}
                                style={styles.cancelBtn}
                            >
                                {text[language].cancel}
                            </button>

                            <button
                                type="submit"
                                style={styles.submitBtn}
                                disabled={loading}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.currentTarget.style.transform = "translateY(-2px)";
                                        e.currentTarget.style.boxShadow = "0 0.5rem 1.5rem rgba(46, 124, 186, 0.4)";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "0 0.25rem 0.75rem rgba(46, 124, 186, 0.3)";
                                }}
                            >
                                {loading ? (
                                    <>
                                        <div style={{
                                            width: "16px",
                                            height: "16px",
                                            border: "2px solid rgba(255,255,255,0.3)",
                                            borderTopColor: "white",
                                            borderRadius: "50%",
                                            animation: "spin 0.8s linear infinite"
                                        }} />
                                        {text[language].loading}
                                    </>
                                ) : (
                                    <>
                                        <GitBranch size={20} />
                                        {text[language].mapEscalation}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

export default EscalationGroupMapping;